import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  ImageIcon,
  X,
  Building2,
  MapPinned,
  Camera,
} from "lucide-react";
import { travelPhotos, availableYears } from "@/data/photos";
import type { TravelPhoto } from "@/data/photos";

/**
 * 照片时间线 / 相册页面
 * 网格布局展示照片，按年份筛选，点击看大图
 */
export default function TimelinePage() {
  const years = ["all", ...availableYears()];
  const [selectedYear, setSelectedYear] = useState<number | "all">("all");
  const [lightbox, setLightbox] = useState<TravelPhoto | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const filteredPhotos = useMemo(() => {
    if (selectedYear === "all") {
      return [...travelPhotos].sort((a, b) => b.date.localeCompare(a.date));
    }
    return travelPhotos
      .filter((p) => p.year === selectedYear)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [selectedYear]);

  /* ── 统计数据 ── */
  const stats = useMemo(() => {
    const cities = new Set(filteredPhotos.map((p) => p.city));
    const provinces = new Set(filteredPhotos.map((p) => p.country));
    return {
      total: filteredPhotos.length,
      cities: cities.size,
      provinces: provinces.size,
    };
  }, [filteredPhotos]);

  /* ── 按日期分组（用于网格中的日期标签） ── */
  const groupedPhotos = useMemo(() => {
    const groups: Record<string, TravelPhoto[]> = {};
    filteredPhotos.forEach((p) => {
      if (!groups[p.date]) groups[p.date] = [];
      groups[p.date].push(p);
    });
    // 按日期倒序
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredPhotos]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "oklch(0.97 0.015 70)", paddingTop: 64 }}>
      {/* Placeholder for fixed header */}
      <div style={{ height: headerHeight }} />

      {/* ===== Header (fixed) ===== */}
      <div
        ref={headerRef}
        className="fixed left-0 right-0 z-30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{
          top: 64,
          padding: "var(--spacing-md) var(--spacing-lg)",
          backgroundColor: "oklch(0.97 0.015 70 / 0.92)",
          backdropFilter: "blur(20px) saturate(1.4)",
          WebkitBackdropFilter: "blur(20px) saturate(1.4)",
          borderBottom: "1px solid oklch(0.90 0.025 65 / 0.4)",
          boxShadow: "0 1px 3px oklch(0.55 0.08 55 / 0.04)",
        }}
      >
        <div className="flex items-center" style={{ gap: "var(--spacing-md)" }}>
          <Link
            to="/"
            className="flex items-center rounded-xl transition-all duration-200 hover:bg-muted"
            style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-xs)" }}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
          </Link>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-handwritten)",
                fontSize: "var(--font-size-title)",
                color: "var(--foreground)",
              }}
            >
              时光相册
            </h1>
            <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
              共 {stats.total} 张照片 · 点击照片查看大图
            </p>
          </div>
        </div>

        {/* Year filter */}
        <div className="flex items-center flex-wrap" style={{ gap: "var(--spacing-xs)" }}>
          {years.map((year) => (
            <motion.button
              key={year}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedYear(year === "all" ? "all" : Number(year))}
              className="rounded-xl transition-all duration-200"
              style={{
                padding: "var(--spacing-xs) var(--spacing-md)",
                fontSize: "var(--font-size-label)",
                fontWeight: selectedYear === year ? 600 : 500,
                backgroundColor:
                  selectedYear === year ? "oklch(0.62 0.18 35)" : "transparent",
                color: selectedYear === year ? "white" : "var(--muted-foreground)",
                boxShadow: selectedYear === year
                  ? "0 4px 12px oklch(0.55 0.15 35 / 0.25)"
                  : "none",
              }}
              onMouseEnter={(e) => {
                if (selectedYear !== year) {
                  e.currentTarget.style.backgroundColor = "oklch(0.62 0.18 35 / 0.08)";
                  e.currentTarget.style.color = "oklch(0.55 0.16 35)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedYear !== year) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--muted-foreground)";
                }
              }}
            >
              {year === "all" ? "全部" : `${year}年`}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="container mx-auto" style={{ padding: "var(--spacing-md) var(--spacing-lg)" }}>
        {/* ===== 统计卡片 ===== */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-4 flex items-center gap-3"
            style={{
              backgroundColor: "oklch(1 0 0)",
              boxShadow: "var(--ds-shadow-sm)",
              border: "1px solid oklch(0.92 0.02 65 / 0.4)",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "oklch(0.62 0.18 35 / 0.1)" }}
            >
              <Camera className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
            </div>
            <div>
              <div className="font-semibold" style={{ fontSize: "var(--font-size-title)", color: "var(--foreground)" }}>
                {stats.total}
              </div>
              <div style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                照片总数
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="rounded-2xl p-4 flex items-center gap-3"
            style={{
              backgroundColor: "oklch(1 0 0)",
              boxShadow: "var(--ds-shadow-sm)",
              border: "1px solid oklch(0.92 0.02 65 / 0.4)",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "oklch(0.55 0.12 240 / 0.1)" }}
            >
              <Building2 className="w-5 h-5" style={{ color: "oklch(0.55 0.12 240)" }} />
            </div>
            <div>
              <div className="font-semibold" style={{ fontSize: "var(--font-size-title)", color: "var(--foreground)" }}>
                {stats.cities}
              </div>
              <div style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                解锁城市
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-4 flex items-center gap-3 col-span-2 sm:col-span-1"
            style={{
              backgroundColor: "oklch(1 0 0)",
              boxShadow: "var(--ds-shadow-sm)",
              border: "1px solid oklch(0.92 0.02 65 / 0.4)",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "oklch(0.78 0.16 65 / 0.12)" }}
            >
              <MapPinned className="w-5 h-5" style={{ color: "oklch(0.78 0.16 65)" }} />
            </div>
            <div>
              <div className="font-semibold" style={{ fontSize: "var(--font-size-title)", color: "var(--foreground)" }}>
                {stats.provinces}
              </div>
              <div style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                省份/直辖市
              </div>
            </div>
          </motion.div>
        </div>

        {/* ===== 照片网格 ===== */}
        <AnimatePresence mode="popLayout">
          {groupedPhotos.length > 0 ? (
            <div className="space-y-6">
              {groupedPhotos.map(([date, photos], groupIndex) => (
                <motion.div
                  key={date}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, delay: groupIndex * 0.03 }}
                >
                  {/* 日期标签 */}
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full"
                      style={{
                        backgroundColor: "oklch(0.62 0.18 35 / 0.08)",
                        color: "oklch(0.62 0.18 35)",
                        fontSize: "var(--font-size-small)",
                        fontWeight: 500,
                      }}
                    >
                      <Calendar className="w-3 h-3" />
                      {date}
                    </div>
                    <div
                      className="flex-1 h-px"
                      style={{ backgroundColor: "oklch(0.90 0.025 65 / 0.4)" }}
                    />
                    <span style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                      {photos.length} 张
                    </span>
                  </div>

                  {/* 照片网格 */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {photos.map((photo, idx) => (
                      <motion.div
                        key={photo.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3, delay: idx * 0.03 }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="relative rounded-2xl overflow-hidden cursor-pointer group"
                        style={{
                          backgroundColor: "oklch(1 0 0)",
                          boxShadow: "var(--ds-shadow-sm)",
                          border: "1px solid oklch(0.92 0.02 65 / 0.3)",
                          aspectRatio: "1 / 1",
                        }}
                        onClick={() => setLightbox(photo)}
                      >
                        <img
                          src={photo.thumbnail}
                          alt={photo.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        {/* Hover overlay */}
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3"
                          style={{
                            background: "linear-gradient(transparent 40%, oklch(0.12 0.04 45 / 0.75) 100%)",
                          }}
                        >
                          <div className="flex items-center gap-1 text-white">
                            <MapPin className="w-3 h-3" />
                            <span className="text-xs font-medium truncate">{photo.city}</span>
                          </div>
                        </div>
                        {/* City badge (always visible, top-left) */}
                        <div
                          className="absolute top-2 left-2 flex items-center rounded-full px-2 py-0.5"
                          style={{
                            gap: 2,
                            backgroundColor: "oklch(0.18 0.06 45 / 0.55)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                            color: "white",
                            fontSize: 10,
                            fontWeight: 500,
                          }}
                        >
                          <MapPin className="w-2.5 h-2.5" />
                          {photo.city}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <ImageIcon className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
              <p style={{ color: "var(--muted-foreground)" }}>该年份暂无照片</p>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* ===== Lightbox (Portal to body) ===== */}
      {createPortal(
        <AnimatePresence>
          {lightbox && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6"
              style={{ backgroundColor: "rgba(15, 12, 10, 0.93)" }}
              onClick={() => setLightbox(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: "#fff",
                  boxShadow: "0 32px 96px rgba(0,0,0,0.5)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={() => setLightbox(null)}
                  className="absolute top-3 right-3 z-20 p-2 rounded-full"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Image */}
                <div
                  className="w-full flex items-center justify-center"
                  style={{ backgroundColor: "#111", minHeight: 240 }}
                >
                  <img
                    src={lightbox.thumbnail}
                    alt={lightbox.title}
                    style={{
                      display: "block",
                      width: "100%",
                      height: "auto",
                      maxHeight: "65vh",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const err = document.createElement("div");
                      err.textContent = "图片加载失败";
                      err.style.color = "#999";
                      err.style.padding = "40px";
                      target.parentElement?.appendChild(err);
                    }}
                  />
                </div>

                {/* Info */}
                <div style={{ padding: 16 }}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: "oklch(0.62 0.18 35)" }} />
                      <h3 className="font-semibold" style={{ fontSize: 16, color: "#333" }}>
                        {lightbox.city} · {lightbox.country}
                      </h3>
                    </div>
                    <span
                      className="flex items-center gap-1 flex-shrink-0"
                      style={{ fontSize: 13, color: "#888" }}
                    >
                      <Calendar className="w-3 h-3" />
                      {lightbox.date}
                    </span>
                  </div>
                  <p
                    className="mt-2"
                    style={{
                      fontSize: 14,
                      color: "#888",
                      wordBreak: "break-all",
                    }}
                  >
                    {lightbox.title}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
